import { magicLinkClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';
import { inferAdditionalFields } from "better-auth/client/plugins";
import { customSessionClient } from "better-auth/client/plugins";

import type { auth as ServerAuth } from "../../../servers/dashboard-server/src/lib/better-auth";

// Cache the auth client instance to prevent recreations
let _authInstance: ReturnType<typeof createAuthClient> | null = null;

function createOptimizedAuthClient() {
    if (_authInstance) return _authInstance;

    _authInstance = createAuthClient({
        baseURL: import.meta.env.VITE_TRPC_SERVER!.replace("/v1/trpc", ""),

        // Optimize fetch options for better performance
        fetchOptions: {
            credentials: 'include',
            // Add connection pooling and timeout optimizations
            keepalive: true,
            // cache: 'no-cache', // Prevent auth response caching
        } as RequestInit,

        // Session configuration optimizations
        session: {
            // Enable client-side session caching for better performance
            cookieCache: {
                enabled: true,
                maxAge: 60 * 5, // 5 minutes cache
            },
            // Optimize session refresh behavior
            updateAge: 60 * 60 * 24, // Only update session once per day
        },

        plugins: [
            magicLinkClient(),
            // Remove usernameClient since it's not being used (commented out in server)
            inferAdditionalFields({
                user: {
                    fullName: {
                        type: "string",
                        required: true,
                    },
                    firstName: {
                        type: "string",
                        required: true,
                        input: true,
                    },
                    lastName: {
                        type: "string",
                        required: true,
                        input: true,
                    },
                    middleName: {
                        type: "string",
                        required: false,
                        input: true,
                    },
                    suffix: {
                        type: "string",
                        required: false,
                        input: true,
                    },
                    image: {
                        type: "string",
                        required: false,
                        input: true,
                    },
                    phone: {
                        type: "string",
                        required: false,
                        input: true,
                    },
                    customFields: {
                        type: "string",
                        required: false,
                        input: false,
                    },
                    isActive: {
                        type: "boolean",
                        required: true,
                        input: false,
                        defaultValue: true,
                    },
                    cartId: {
                        type: "string",
                        required: false,
                        input: false,
                    },
                    defaultConversationId: {
                        type: "string",
                        required: false,
                        input: false,
                    },
                    createdAt: {
                        type: "date",
                        required: false,
                        input: false,
                    },
                    updatedAt: {
                        type: "date",
                        required: false,
                        input: false,
                    },
                    roles: {
                        type: "string",
                        required: false,
                        input: false,
                    },
                    permissions: {
                        type: "string",
                        required: false,
                        input: false,
                    },
                }
            }),
            customSessionClient<typeof ServerAuth>()
        ],
    });

    return _authInstance;
}

export const auth = createOptimizedAuthClient();
